import React, { useState } from 'react';
import { message } from 'antd';
import axios from 'axios';
import PropTypes from 'prop-types';
import OttoPDFChatHeaderActions from './OttoPDFChatHeaderActions';
import { OTTO_PDF_INITIAL_MESSAGE } from '../common/pdf/PDFDesigner/PDFDesigner.constants';

import { request } from '../helpers/requests';
import { uploadFilesToS3 } from '../files/state/files.service';

import OttoChatBase from './OttoChatBase';

function OttoPDFChat({
  visible,
  onClose,
  formData,
  completedForm,
  currentDrawOptions,
  onDrawOptionsUpdate,
  companySettings,
  onRestore,
  onUndo,
  canUndo = false,
}) {
  const [uploadedFile, setUploadedFile] = useState(null);

  const handleFileUpload = async (file) => {
    try {
      if (!file) return;

      const filesToUpload = [{
        jsFileObject: file,
        path: '',
      }];

      const uploadedFileData = await uploadFilesToS3(filesToUpload);

      if (uploadedFileData.length > 0) {
        setUploadedFile(uploadedFileData[0]);
        message.success('File uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error('Failed to upload file. Please try again.');
    }
  };

  const handleRemoveFile = (fileId) => {
    if (uploadedFile && (!fileId || uploadedFile.id === fileId)) {
      setUploadedFile(null);
    }
  };

  const handleSend = async (messageText, threadId, fileFromBase) => {
    // Prepare the payload with PDF-specific context
    const payload = {
      message: messageText,
      formData: formData || {},
      completedForm: completedForm || {},
      currentDrawOptions: currentDrawOptions || [],
      companySettings: companySettings || {},
    };

    if (fileFromBase) {
      payload.uploadedFile = {
        id: fileFromBase.id,
        name: fileFromBase.name,
        path: fileFromBase.path,
      };
    }

    if (threadId) {
      payload.threadId = threadId;
    }

    const res = await request({
      call: () => (
        axios.post('/otto/pdf-design', payload)
      ),
      successMsg: 'Message sent',
      errMsg: 'Failed to send message',
    });

    const response = res?.data;

    // If the response contains new drawOptions, apply them
    if (response?.drawOptions && onDrawOptionsUpdate) {
      try {
        onDrawOptionsUpdate(response.drawOptions);
        message.success('PDF layout updated successfully!');
      } catch (error) {
        console.error('Error applying drawOptions:', error);
        message.error('Failed to apply PDF layout changes');
      }
    }

    if (fileFromBase) {
      setUploadedFile(null);
    }

    return {
      threadId: response?.threadId,
      response: response?.response,
    };
  };

  const headerActions = ({ addMessage }) => (
    <OttoPDFChatHeaderActions
      onUndo={onUndo}
      onRestore={onRestore}
      canUndo={canUndo}
      addMessage={addMessage}
    />
  );

  return (
    <OttoChatBase
      visible={visible}
      onClose={onClose}
      title="Otto: PDF Designer"
      placeholder="Describe your PDF design needs..."
      loadingText="Otto is designing your PDF..."
      headerActions={headerActions}
      onSend={handleSend}
      initialMessage={OTTO_PDF_INITIAL_MESSAGE}
      defaultWidth="clamp(280px, 30vw, 480px)"
      fullscreenWidth="calc(100vw - 220px)"
      onFileUpload={handleFileUpload}
      uploadedFile={uploadedFile}
      onRemoveFile={handleRemoveFile}
    />
  );
}

OttoPDFChat.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.object,
  completedForm: PropTypes.object,
  currentDrawOptions: PropTypes.array,
  onDrawOptionsUpdate: PropTypes.func.isRequired,
  companySettings: PropTypes.object,
  onRestore: PropTypes.func,
  onUndo: PropTypes.func,
  canUndo: PropTypes.bool,
};

export default OttoPDFChat;
